/**
 * Model Context Protocol (MCP) configuration page.
 * Manages MCP endpoint and API keys for LLM integration.
 *
 * @module pages/MCPPage
 */

import { useState, useEffect } from 'react';
import { Copy, Key, Plus, Server, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { PageHeader } from '@/components/layout';
import { Card, CardHeader, CardBody, Button, Modal, Badge } from '@/components/ui';
import { httpClient } from '@/services';
import { config } from '@/config';
import { showErrorToast } from '@/utils/errorToast';

interface ApiKeyInfo {
  keyId: string;
  name: string | null;
  createdOn: string;
  expiresAt: string | null;
  lastUsedOn: string | null;
}

/**
 * MCP configuration page
 */
export function MCPPage() {
  // API Key management state
  const [apiKeys, setApiKeys] = useState<ApiKeyInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [deletingKeyId, setDeletingKeyId] = useState<string | null>(null);

  // Load API keys on mount
  useEffect(() => {
    loadApiKeys();
  }, []);

  const loadApiKeys = async () => {
    try {
      setIsLoading(true);
      const response = await httpClient.get('/apikeys');
      setApiKeys(response.data || []);
    } catch (error) {
      console.error('Failed to load API keys:', error);
      toast.error('Failed to load API keys');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateKey = async () => {
    setIsGenerating(true);

    try {
      const response = await httpClient.post('/apikeys', {
        name: newKeyName || null,
      });

      const keyValue = response.data?.apiKey;

      if (!keyValue || typeof keyValue !== 'string') {
        throw new Error('Invalid API response: no key found');
      }

      setGeneratedKey(keyValue);
      setShowCreateModal(false);
      setShowKeyModal(true);
      setNewKeyName('');

      // Reload the key list
      await loadApiKeys();

      toast.success('API key created successfully');
    } catch (error) {
      console.error('Failed to create API key:', error);
      showErrorToast('Failed to create API key. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDeleteKey = async (keyId: string) => {
    setDeletingKeyId(keyId);

    try {
      await httpClient.delete(`/apikeys/${keyId}`);
      setApiKeys((prev) => prev.filter((k) => k.keyId !== keyId));
      toast.success('API key deleted');
    } catch (error) {
      console.error('Failed to delete API key:', error);
      toast.error('Failed to delete API key');
    } finally {
      setDeletingKeyId(null);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const getMcpEndpoint = () => {
    return `${config.apiBaseUrl}/mcp`;
  };

  const isExpired = (expiresAt: string | null) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div>
      <PageHeader
        title="Model Context Protocol"
        description="Configure MCP endpoint and manage API keys for LLM integration"
        breadcrumbs={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'MCP' }]}
      />

      <div className="max-w-2xl space-y-6">
        {/* MCP Configuration */}
        <Card>
          <CardHeader
            title="MCP Endpoint"
            description="Connect your LLMs to ADC via Model Context Protocol"
            icon={<Server className="h-5 w-5" />}
          />
          <CardBody className="space-y-4">
            <div>
              <label
                htmlFor="mcp-endpoint-url"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Endpoint URL
              </label>
              <div className="flex gap-2">
                <input
                  id="mcp-endpoint-url"
                  type="text"
                  readOnly
                  value={getMcpEndpoint()}
                  className="flex-1 px-3 py-2 bg-gray-50 dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#2a2a2a] rounded-md text-sm font-mono text-gray-900 dark:text-gray-200"
                />
                <Button
                  variant="secondary"
                  onClick={() => copyToClipboard(getMcpEndpoint(), 'MCP endpoint')}
                  leftIcon={<Copy className="h-4 w-4" />}
                >
                  Copy
                </Button>
              </div>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                Use this endpoint in your LLM configuration to enable MCP integration. You'll also
                need an API key (see below).
              </p>
            </div>

            <div className="pt-4 border-t border-gray-200 dark:border-[#2a2a2a]">
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-200 mb-3">
                Example MCP Configuration
              </h4>
              <pre className="bg-gray-50 dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#2a2a2a] rounded-md p-3 text-xs overflow-x-auto text-gray-800 dark:text-gray-200">
                {`{
  "mcpServers": {
    "adc": {
      "type": "http",
      "url": "${getMcpEndpoint()}",
      "headers": {
        "x-ms-api-key": "YOUR_API_KEY"
      }
    }
  }
}`}
              </pre>
            </div>
          </CardBody>
        </Card>

        {/* API Key Management */}
        <Card>
          <CardHeader
            title="API Keys"
            description="Manage API keys for programmatic access"
            icon={<Key className="h-5 w-5" />}
            action={
              <Button
                onClick={() => setShowCreateModal(true)}
                leftIcon={<Plus className="h-4 w-4" />}
              >
                Create Key
              </Button>
            }
          />
          <CardBody>
            {isLoading ? (
              <div className="text-center py-8">
                <p className="text-sm text-gray-500 dark:text-gray-400">Loading...</p>
              </div>
            ) : apiKeys.length === 0 ? (
              <div className="text-center py-8">
                <Key className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" />
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-200">
                  No API keys
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Create an API key to get started.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {apiKeys.map((key) => (
                  <div
                    key={key.keyId}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#2a2a2a] rounded-md"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-200">
                          {key.name || 'Unnamed Key'}
                        </h4>
                        {isExpired(key.expiresAt) ? (
                          <Badge variant="danger">Expired</Badge>
                        ) : (
                          <Badge variant="success">Active</Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Created {formatDate(key.createdOn)}
                        {key.expiresAt && ` • Expires ${formatDate(key.expiresAt)}`}
                        {key.lastUsedOn && ` • Last used ${formatDate(key.lastUsedOn)}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteKey(key.keyId)}
                        isLoading={deletingKeyId === key.keyId}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Create API Key Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setNewKeyName('');
        }}
        title="Create API Key"
      >
        <div className="space-y-4">
          <div>
            <label
              htmlFor="mcp-key-name"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Key Name (optional)
            </label>
            <input
              id="mcp-key-name"
              type="text"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              placeholder="e.g., My CI/CD Key"
              className="w-full px-3 py-2 bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#2a2a2a] rounded-md text-sm text-gray-900 dark:text-gray-200"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setShowCreateModal(false);
                setNewKeyName('');
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleCreateKey} isLoading={isGenerating}>
              Create
            </Button>
          </div>
        </div>
      </Modal>

      {/* Generated API Key Modal */}
      {generatedKey && (
        <Modal
          isOpen={showKeyModal}
          onClose={() => {
            setShowKeyModal(false);
            setGeneratedKey(null);
          }}
          title="API Key Created"
        >
          <div className="space-y-4">
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                <strong>Important:</strong> Copy this API key now. You won't be able to see it
                again!
              </p>
            </div>
            <div>
              <label
                htmlFor="mcp-generated-key"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Your new API key
              </label>
              <div className="flex gap-2">
                <input
                  id="mcp-generated-key"
                  type="text"
                  readOnly
                  value={generatedKey}
                  className="flex-1 px-3 py-2 bg-gray-50 dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#2a2a2a] rounded-md text-sm font-mono text-gray-900 dark:text-gray-200"
                />
                <Button
                  variant="secondary"
                  onClick={() => copyToClipboard(generatedKey, 'API key')}
                  leftIcon={<Copy className="h-4 w-4" />}
                >
                  Copy
                </Button>
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                onClick={() => {
                  setShowKeyModal(false);
                  setGeneratedKey(null);
                }}
              >
                Done
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
